import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { toast } from 'sonner'
import type { InfiniteData } from '@tanstack/react-query'
import { Share } from '@capacitor/share'

import * as Icons from '@icons'
import { IoTClient } from '@/_mqtt/config'
import { hasRestrictedUserLimits, parseUserLimits } from '@/_schemas'
import { VirtualCardAPI } from '@apis'
import { Column } from '@components/templates/column'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import AccountLimitsBanner from '@components/molecules/account-limits-banner'
import DashboardBanner from '@components/molecules/dashboard-banner'
import GroupCell from '@components/molecules/group-cell'
import { type ReferralCardVariant } from '@components/molecules/referral-card'
import Spinner from '@components/atoms/spinner'
import TabGroup from '@components/molecules/tab-group'
import ServiceOutageModal from '@components/organisims/service-outage-modal'
import { useHasHistoricalGroups } from '@hooks/tanstack/useHasHistoricalGroups'
import { useUserCredits } from '@hooks/tanstack/useUserCredits'
import { useUserReferral } from '@hooks/tanstack/useUserReferral'
import { useShareSheet } from '@components/organisims/share-card'
import { platform } from '@utils/capacitor-helper'
import { priceFormatter } from '@utils/number-helper'
import { isUnclaimedCredit } from '@utils/promo-helper'
import { queryClient } from '@/lib/queryClient'
import type { RouteObject } from 'react-router-dom'

import { useGlobalSettings } from '../../_globalStore/_hooks'
import { useUserGroups } from '../../../hooks/tanstack/useUserGroups'
import NewUserLanding from './_components/new-user-landing'
import './dashboard.css'

enum Tabs {
    Active = 'active',
    Completed = 'completed',
    Canceled = 'canceled',
}

const pageSize = 10
const tabs = Object.values(Tabs).map((value) => ({ name: value }))

const AccountDashboardScreen: FC = () => {
    const { isLoaded, user } = useUser()
    const { data: referralData } = useUserReferral(user?.id)
    const { data: userCredits } = useUserCredits(user?.id)
    const [searchParams, setSearchParams] = useSearchParams()
    const { setShowParticipantJoinedModal, state: { showParticipantJoinedModal } } = useGlobalSettings()
    const navigate = useNavigate()

    const [showOutage, setServiceOutage] = useState(false)
    const userId = user?.id || ''
    const showCreditAmountReferral = user?.publicMetadata?.referralUser === true
    const userLimits = useMemo(() => parseUserLimits(user?.publicMetadata?.limits), [user?.publicMetadata?.limits])
    const hasAccountLimits = useMemo(() => hasRestrictedUserLimits(userLimits), [userLimits])
    const firstGroupPromoCredit = useMemo(() => (
        userCredits?.status === 'successful'
            ? userCredits.credits.find((credit) => (
                credit.action === 'first_group' && isUnclaimedCredit(credit.claimedAt)
            ))
            : undefined
    ), [userCredits])
    const hasUnclaimedPromoCredit = !!firstGroupPromoCredit
    const firstGroupPromoAmount = firstGroupPromoCredit?.amount || 0
    const firstGroupParticipantCount = firstGroupPromoCredit?.requirements?.participantCount || 3
    const firstGroupGroupSpend = firstGroupPromoCredit?.requirements?.groupSpend || 10000
    const formattedFirstGroupPromoAmount = priceFormatter.format(firstGroupPromoAmount / 100)
    const formattedFirstGroupGroupSpend = priceFormatter.format(firstGroupGroupSpend / 100)
    const creditAmount = Number(user?.publicMetadata?.creditAmount || 0)
    const hasCreditAmount = Number.isFinite(creditAmount) && creditAmount > 0
    const referralDialogRef = useRef<HTMLDialogElement>(null)
    const spinnerDialogRef = useRef<HTMLDialogElement>(null)
    const [referralDialogVariant, setReferralDialogVariant] = useState<ReferralCardVariant>('creditAmount')

    const status = useMemo(() => {
        const statusParam = searchParams.get('status')
        return VirtualCardAPI.GetUserGroupsRequest.statuses.find((item) => item === statusParam) || 'active'
    }, [searchParams])
    const statusesToCheckForHistory = useMemo(() => (
        VirtualCardAPI.GetUserGroupsRequest.statuses.filter((item) => item !== status)
    ), [status])

    const { data, fetchNextPage, hasNextPage, isFetching, isError, isPending, refetch } = useUserGroups(userId, status, pageSize)
    const groups = useMemo(() => data?.pages.flat() || [], [data])
    const shouldCheckHistoricalGroups = isLoaded && !!userId && groups.length < 1 && !isPending && !isError
    const {
        hasHistoricalGroups,
        isError: isHistoricalGroupsError,
        isPending: isHistoricalGroupsPending,
        refetch: refetchHistoricalGroups
    } = useHasHistoricalGroups(userId, shouldCheckHistoricalGroups, statusesToCheckForHistory)
    const hasAnyGroups = groups.length > 0 || hasHistoricalGroups
    const showDashboardLoadingState = !isLoaded || (groups.length < 1 && (isPending || (shouldCheckHistoricalGroups && isHistoricalGroupsPending)))
    const showDashboardErrorState = isLoaded && groups.length < 1 && (isError || isHistoricalGroupsError)
    const shouldShowNewUserLanding = isLoaded && groups.length < 1 && !showDashboardLoadingState && !showDashboardErrorState && !hasAnyGroups
    const shouldShowReturningEmptyState = status === 'active' && isLoaded && groups.length < 1 && !showDashboardLoadingState && !showDashboardErrorState && hasAnyGroups
    const shouldShowCompletedEmptyState = status === 'completed' && isLoaded && groups.length < 1 && !showDashboardLoadingState && !showDashboardErrorState && hasAnyGroups
    const shouldShowCanceledEmptyState = status === 'canceled' && isLoaded && groups.length < 1 && !showDashboardLoadingState && !showDashboardErrorState && hasAnyGroups
    const newUserBannerVariant = useMemo<'promo' | 'referral' | 'welcome'>(() => {
        if (showCreditAmountReferral && hasCreditAmount) return 'referral'
        if (hasUnclaimedPromoCredit) return 'promo'
        return 'welcome'
    }, [showCreditAmountReferral, hasCreditAmount, hasUnclaimedPromoCredit])

    useEffect(() => {
        if (showParticipantJoinedModal) {
            openParticipantJoined()
            setShowParticipantJoinedModal(false)
        }
    }, [setShowParticipantJoinedModal, showParticipantJoinedModal])

    const { activeGroups, fundedGroups, openGroups } = useMemo(() => groups.reduce<{ activeGroups: Group[], fundedGroups: Group[], openGroups: Group[] }>((prev, group) => {
        let ledgerTotal = 0

        if (group.ledger) {
            ledgerTotal = group.ledger.reduce(
                (transactionSum, transaction) => transactionSum + transaction.breakdown.reduce((sum, item) => sum + item.amount, 0), 0
            )
        }

        const isLocked = group.closedAt !== "0001-01-01T00:00:00Z"
        const isCompleted = (group.completedAt !== "0001-01-01T00:00:00Z" && (ledgerTotal === group.spendAmount)) || group.finalizedAt !== '0001-01-01T00:00:00Z'
        const isPending = group.completedAt !== "0001-01-01T00:00:00Z" && group.finalizedAt === '0001-01-01T00:00:00Z' && (ledgerTotal !== group.spendAmount) && group.organizer.id === userId
        const isCanceled = group.canceledAt !== "0001-01-01T00:00:00Z"
        const isActivated = !isCompleted && !isCanceled && isLocked
        const isFunded = !isCompleted && !isCanceled && !isActivated && !!group.collectedAmount && !!group.targetAmount && (group.collectedAmount >= group.targetAmount)

        if (isActivated || isPending) return { ...prev, activeGroups: [...prev.activeGroups, group] }
        else if (isFunded) return { ...prev, fundedGroups: [...prev.fundedGroups, group] }
        else return { ...prev, openGroups: [...prev.openGroups, group] }
    }, { activeGroups: [], fundedGroups: [], openGroups: [] }), [groups, userId])

    const handleScroll = useCallback(async () => {
        if (hasNextPage && !isFetching && !isError) {
            const isNearBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 20
            if (isNearBottom) fetchNextPage()
        }
    }, [hasNextPage, isFetching, isError, fetchNextPage])

    const ref = useRef<HTMLDialogElement>(null)
    function openParticipantJoined() { if (ref.current) ref.current.showModal() }
    function closeParticipantJoined() { if (ref.current) ref.current.close() }

    // const explainerRef = useRef<HTMLDialogElement>(null)
    const ineligibleRef = useRef<HTMLDialogElement>(null)
    // const location = useLocation()
    // useEffect(() => {
    //     if (location.state?.showExplainer) {
    //         explainerRef.current?.showModal()
    //         // Clear the state so modal doesn't reopen on refresh
    //         navigate(location.pathname, { replace: true, state: {} })
    //     }
    // }, [location.state, navigate, location.pathname])
    // function closeExplainer() { explainerRef.current?.close() }

    // Check for referral=ineligible query param to show not eligible modal
    useEffect(() => {
        if (searchParams.get('referral') === 'ineligible') {
            ineligibleRef.current?.showModal()
        }
    }, [searchParams])

    function closeIneligibleModal() {
        ineligibleRef.current?.close()
        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.delete('referral')
        setSearchParams(nextSearchParams, { replace: true })
    }

    useEffect(() => {
        window.addEventListener("scroll", handleScroll)
        return () => window.removeEventListener("scroll", handleScroll)
    }, [handleScroll])

    // Subscribe to IoT updates for all user groups to keep the dashboard "live"
    useEffect(() => {
        if (!userId || groups.length === 0 || status !== 'active') return

        const iotClient = IoTClient.getInstance(userId)
        const updateGroupInCache = (groupId: string, updatedGroup: Group) => {
            queryClient.setQueryData<InfiniteData<Group[], unknown>>(
                ['user-groups', userId, status],
                (currentData) => {
                    if (!currentData) return currentData

                    let wasUpdated = false
                    const pages = currentData.pages.map((page) => page.map((group) => {
                        if (group.id !== groupId) return group
                        wasUpdated = true
                        return { ...updatedGroup, id: group.id }
                    }))

                    if (!wasUpdated) {
                        console.warn('Dashboard: Group Update Missed Cache Match', {
                            topicGroupId: groupId
                        })
                        return currentData
                    }
                    return { ...currentData, pages }
                }
            )
        }

        const inFlightGroupFetches = new Set<string>()
        const callbacksByGroupId = new Map<string, (updatedGroup: Omit<Group, 'pledges' | 'transactions'>) => void>()

        // Subscribe to each group's channel; treat MQTT payload as an update signal and fetch canonical group data.
        groups.forEach((group) => {
            const callback = async (updatedGroup: Omit<Group, 'pledges' | 'transactions'>) => {
                console.log('Dashboard: Group Update Received:', {
                    topicGroupId: group.id,
                    payloadGroupId: updatedGroup.id
                })

                if (inFlightGroupFetches.has(group.id)) return
                inFlightGroupFetches.add(group.id)

                try {
                    const freshGroup = await VirtualCardAPI.GetGroupRequest.fetch(group.id)
                    updateGroupInCache(group.id, freshGroup)
                } catch (error) {
                    console.error('Dashboard: Failed to refresh updated group', { groupId: group.id, error })
                } finally {
                    inFlightGroupFetches.delete(group.id)
                }
            }

            callbacksByGroupId.set(group.id, callback)
            iotClient.addTopicListener('/group/*/', callback, { wildcard: group.id })
        })

        return () => {
            // Cleanup all listeners
            groups.forEach((group) => {
                const callback = callbacksByGroupId.get(group.id)
                if (callback) iotClient.removeTopicListener('/group/*/', callback, { wildcard: group.id })
            })
        }
    }, [groups, status, userId])

    const referralUrl = useMemo(() => {
        if (!referralData || referralData.status !== 'successful' || !referralData.referral?.id) return ''
        const baseUrl = import.meta.env.DEV
            ? 'https://dev.group.splitcard.com'
            : 'https://group.splitcard.com'
        return `${baseUrl}/referral/${referralData.referral.id}`
    }, [referralData])
    const { canShare, share } = useShareSheet({ url: referralUrl })

    function handleShareReferralLink() {
        if (platform === 'web') {
            if (canShare) share();
            else window.navigator.clipboard.writeText(referralUrl)
                .then(() => toast.success('Link Copied'))
                .catch(() => toast.error('Failed to copy link'))
        } else {
            Share.canShare()
                .then(result => {
                    if (result.value) Share.share({
                        url: referralUrl,
                        title: 'Join me on Splitcard!'
                    })
                })
        }
    }

    function openReferralDialog(variant: ReferralCardVariant = 'creditAmount') {
        setReferralDialogVariant(variant)
        if (referralDialogRef.current) referralDialogRef.current.showModal()
    }

    function closeReferralDialog() {
        if (referralDialogRef.current) referralDialogRef.current.close()
    }

    function handleStartFirstGroup() {
        closeReferralDialog()
    }

    function handleLandingBannerClick() {
        if (showCreditAmountReferral && hasCreditAmount) {
            openReferralDialog('creditAmount')
            return
        }

        if (hasUnclaimedPromoCredit) {
            openReferralDialog('promo')
        }
    }

    function handleRetryDashboard() {
        refetch()
        if (shouldCheckHistoricalGroups || isHistoricalGroupsError) refetchHistoricalGroups()
    }

    return (
        <Column.Content className={shouldShowNewUserLanding ? 'account-dashboard-screen account-dashboard-screen-new-user' : 'account-dashboard-screen'}>
            {!shouldShowNewUserLanding && hasAnyGroups && (
                <div className='flex flex-row flex-wrap items-center justify-center'>
                    <TabGroup tabs={tabs} activeTab={status} onTabChange={(tab) => { setSearchParams({ status: tab.toLowerCase() }) }} />
                </div>
            )}
            {hasAccountLimits && status === 'active' && !showDashboardLoadingState && <AccountLimitsBanner limits={user?.publicMetadata?.limits} />}
            {shouldShowNewUserLanding ? (
                <NewUserLanding
                    bannerVariant={newUserBannerVariant}
                    promoAmount={firstGroupPromoAmount}
                    groupSpendRequirement={firstGroupGroupSpend}
                    participantCountRequirement={firstGroupParticipantCount}
                    onBannerClick={(showCreditAmountReferral && hasCreditAmount) || hasUnclaimedPromoCredit ? handleLandingBannerClick : undefined}
                    onTryDemo={() => navigate('/group/demo')}
                />
            ) : (<>
                {status === 'active' && !showDashboardLoadingState && hasUnclaimedPromoCredit && !showCreditAmountReferral && (
                    <DashboardBanner
                        variant='promo'
                        promoAmount={firstGroupPromoAmount}
                        groupSpendRequirement={firstGroupGroupSpend}
                        participantCountRequirement={firstGroupParticipantCount}
                        onClick={() => openReferralDialog('promo')}
                    />
                )}
                {status === 'active' && !showDashboardLoadingState && showCreditAmountReferral && hasCreditAmount && <DashboardBanner variant='creditAmount' creditAmount={creditAmount} onClick={() => openReferralDialog('creditAmount')} />}
                {!!groups.length && status === 'active' && (<>
                    {!!activeGroups.length && (<>
                        <h4 className='text-center'>Activated Groups</h4>
                        {activeGroups.map((group, index) => (
                            <GroupCell key={`${group.id}-${index}`} group={group} />
                        ))}
                    </>)}
                    {!!fundedGroups.length && (<>
                        <div className='flex flex-row justify-between'>
                            <h4 className='text-center'>Funded Groups</h4>
                            <p className='text-body text-gray-500'>{fundedGroups.length} Group{fundedGroups.length > 1 && 's'}</p>
                        </div>
                        {fundedGroups.map((group, index) => (
                            <GroupCell key={`${group.id}-${index}`} group={group} />
                        ))}
                    </>)}
                    {!!openGroups.length && (<>
                        <div className='flex flex-row justify-between'>
                            <h4 className='text-center'>In Progress Groups</h4>
                            <p className='text-body text-gray-500'>{openGroups.length} Group{openGroups.length > 1 && 's'}</p>
                        </div>
                        {openGroups.map((group, index) => (
                            <GroupCell key={`${group.id}-${index}`} group={group} />
                        ))}
                    </>)}
                    <div className='self-center mt-4 text-center'>
                        {(!hasNextPage && groups.length >= pageSize) && <p className='text-gray-500'><strong>End of List</strong></p>}
                        {isFetching && <Spinner indicator='circle' />}
                        {(isError && !isFetching) && (<>
                            <p className='text-red-500'>{'Failed to fetch groups'}</p>
                            <a onClick={() => refetch()}>retry</a>
                        </>)}
                    </div>
                </>)}
                {!!groups.length && status === 'completed' && (<>
                    {groups.map((group, index) => (
                        <GroupCell key={`${group.id}-${index}`} group={group} />
                    ))}
                    <div className='self-center mt-4 text-center'>
                        {(!hasNextPage && groups.length >= pageSize) && <p className='text-gray-500'><strong>End of List</strong></p>}
                        {isFetching && <Spinner indicator='circle' />}
                        {(isError && !isFetching) && (<>
                            <p className='text-red-500'>{'Failed to fetch groups'}</p>
                            <a onClick={() => refetch()}>retry</a>
                        </>)}
                    </div>
                </>)}
                {!!groups.length && status === 'canceled' && (<>
                    {groups.map((group, index) => (
                        <GroupCell key={`${group.id}-${index}`} group={group} />
                    ))}
                    <div className='self-center mt-4 text-center'>
                        {(!hasNextPage && groups.length >= pageSize) && <p className='text-gray-500'><strong>End of List</strong></p>}
                        {isFetching && <Spinner indicator='circle' />}
                        {(isError && !isFetching) && (<>
                            <p className='text-red-500'>{'Failed to fetch groups'}</p>
                            <a onClick={() => refetch()}>retry</a>
                        </>)}
                    </div>
                </>)}
                {showDashboardLoadingState && (
                    <div className='account-dashboard-loading-state'>
                        <Spinner indicator='circle' />
                    </div>
                )}
                {showDashboardErrorState && (
                    <div className='account-dashboard-error-state'>
                        <div className='account-dashboard-error-card'>
                            <h2>We Couldn&apos;t Load Your Groups</h2>
                            <p className='text-gray-500'>Please try again. If the issue continues, contact support.</p>
                            <Button theme='secondary' onClick={handleRetryDashboard}>Retry</Button>
                        </div>
                    </div>
                )}
                {shouldShowCompletedEmptyState && (
                    <Card style={{ background: 'var(--splitcard-white)', border: '2px solid var(--splitcard-gray-200)', marginTop: '1rem' }}>
                        <h2>You Haven&apos;t Completed Any Groups</h2>
                        <p className='text-gray-500'>Activate a group, make your purchase and finalize.</p>
                    </Card>
                )}
                {shouldShowCanceledEmptyState && (
                    <Card style={{ background: 'var(--splitcard-white)', border: '2px solid var(--splitcard-gray-200)', marginTop: '1rem' }}>
                        <h2>You Haven&apos;t Canceled Any Groups</h2>
                        <p className='text-gray-500'>If you or someone else cancels a group it will appear here.</p>
                    </Card>
                )}
                {shouldShowReturningEmptyState && (
                    <div className='account-dashboard-empty-state'>
                        <div className='flex flex-col gap-4 mx-auto text-center'>
                            <h2 className='text-blue-500'>No Active Groups</h2>
                            <p className='text-body'>Tap below to start a new group</p>
                        </div>
                    </div>
                )}
            </>)}
            <dialog ref={ref}>
                <div>
                    <div className="flex flex-col items-stretch" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                        <div className='flex flex-row items-center gap-4'>
                            <Icons.Check type="bordered" height="3.5rem" width="3.5rem" style={{ alignSelf: 'flex-start', color: 'var(--splitcard-green-500)' }} />
                            <h2>You Have Successfully Joined The Group!</h2>
                        </div>
                        <p>You will receive text notifications about group activity and when your credit card is charged!</p>
                        <div className="flex flex-col items-stretch gap-2.5">
                            <Button theme="primary" formMethod="dialog" onClick={closeParticipantJoined}>Got it!</Button>
                        </div>
                    </div>
                </div>
            </dialog>
            <dialog ref={referralDialogRef}>
                <div>
                    {(referralDialogVariant === 'promo') ? (
                        <div style={{ width: 'calc(var(--max-content-width, 610px) - 4rem)', maxWidth: 'calc(100vw - 2rem)', background: 'var(--splitcard-white)', margin: '1rem', borderRadius: 'var(--border-radius-lrg)', padding: '2rem', textAlign: 'center', position: 'relative', overflowY: 'auto' }}>
                            <button
                                onClick={closeReferralDialog}
                                style={{
                                    position: 'absolute',
                                    top: '1rem',
                                    right: '1rem',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                aria-label="Close dialog"
                            >
                                <Icons.Close width="1.25rem" height="1.25rem" tint="var(--splitcard-gray-500)" />
                            </button>

                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                                <div style={{
                                    background: 'linear-gradient(135deg, #20c997 0%, #17a2b8 100%)',
                                    borderRadius: '1rem',
                                    padding: '1.25rem',
                                    display: 'inline-flex'
                                }}>
                                    <Icons.GiftBox width="2.5rem" height="2.5rem" tint="white" />
                                </div>
                            </div>

                            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem' }}>Earn {formattedFirstGroupPromoAmount} In Credits</h2>
                            <p style={{ color: 'var(--splitcard-gray-600)', marginBottom: '1rem', fontSize: '0.875rem' }}>
                                Start your first group, reach <strong style={{ color: 'var(--splitcard-green-600)' }}>{firstGroupParticipantCount} total people</strong>, and spend <strong style={{ color: 'var(--splitcard-green-600)' }}>{formattedFirstGroupGroupSpend}</strong> to earn <strong style={{ color: 'var(--splitcard-green-600)' }}>{formattedFirstGroupPromoAmount} in credits.</strong>
                            </p>

                            <ol style={{
                                listStyle: 'none',
                                margin: 0,
                                padding: 0,
                                textAlign: 'left',
                                marginBottom: '2rem',
                                display: 'grid',
                                gridAutoRows: '1fr',
                                gap: '2rem',
                                '--badge-size': '2.85rem',
                                '--line-width': '2px'
                            } as React.CSSProperties}>
                                <li style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'var(--badge-size) 1fr',
                                    columnGap: '1rem',
                                    position: 'relative',
                                    alignItems: 'stretch'
                                }}>
                                    <div style={{
                                        background: '#EBF5FF',
                                        borderRadius: '0.5rem',
                                        padding: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '2.85rem',
                                        width: '2.85rem',
                                        height: '2.85rem',
                                        position: 'relative',
                                        zIndex: 2,
                                        border: '1px solid #BFDBFE'
                                    }}>
                                        <Icons.AddUser width="1.45rem" height="1.45rem" tint="#3B82F6" />
                                        <div style={{
                                            content: '""',
                                            position: 'absolute',
                                            left: 'calc(50% - 1px)',
                                            top: '100%',
                                            bottom: 'calc(-100% - 2rem)',
                                            width: '2px',
                                            background: 'var(--splitcard-gray-100)',
                                            zIndex: 0
                                        }} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', minHeight: '2.85rem' }}>
                                        <h4 style={{ fontSize: '0.875rem', fontWeight: '700', margin: 0, lineHeight: 1.2 }}>Start Your First Group</h4>
                                    </div>
                                </li>

                                <li style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'var(--badge-size) 1fr',
                                    columnGap: '1rem',
                                    position: 'relative',
                                    alignItems: 'stretch'
                                }}>
                                    <div style={{
                                        background: '#F3E8FF',
                                        borderRadius: '0.5rem',
                                        padding: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '2.85rem',
                                        width: '2.85rem',
                                        height: '2.85rem',
                                        position: 'relative',
                                        zIndex: 2,
                                        border: '1px solid #E9D5FF'
                                    }}>
                                        <Icons.Group width="1.45rem" height="1.45rem" tint="#A855F7" />
                                        <div style={{
                                            content: '""',
                                            position: 'absolute',
                                            left: 'calc(50% - 1px)',
                                            top: '100%',
                                            bottom: 'calc(-100% - 2rem)',
                                            width: '2px',
                                            background: 'var(--splitcard-gray-100)',
                                            zIndex: 0
                                        }} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', minHeight: '2.85rem' }}>
                                        <h4 style={{ fontSize: '0.875rem', fontWeight: '700', margin: 0, lineHeight: 1.2 }}>Reach {firstGroupParticipantCount} Total People</h4>
                                    </div>
                                </li>

                                <li style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'var(--badge-size) 1fr',
                                    columnGap: '1rem',
                                    position: 'relative',
                                    alignItems: 'stretch'
                                }}>
                                    <div style={{
                                        background: '#D1FAE5',
                                        borderRadius: '0.5rem',
                                        padding: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '2.85rem',
                                        width: '2.85rem',
                                        height: '2.85rem',
                                        position: 'relative',
                                        zIndex: 2,
                                        border: '1px solid #86EFAC'
                                    }}>
                                        <Icons.Dollar width="1.45rem" height="1.45rem" tint="#10B981" />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', minHeight: '2.85rem' }}>
                                        <h4 style={{ fontSize: '0.875rem', fontWeight: '700', margin: 0, lineHeight: 1.2 }}>Spend {formattedFirstGroupGroupSpend} On The Group Card</h4>
                                    </div>
                                </li>
                            </ol>

                            <button
                                onClick={handleStartFirstGroup}
                                style={{
                                    width: '100%',
                                    background: 'var(--splitcard-black)',
                                    color: 'var(--splitcard-white)',
                                    border: 'none',
                                    padding: '1rem',
                                    borderRadius: 'var(--border-radius-lrg)',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.75rem'
                                }}
                            >
                                Start First Group
                                <Icons.Arrow width='1rem' height='1rem' facing='right' strokeWidth='2px' />
                            </button>
                            {/* <p className='text-body-2 text-gray-500 text-center'>Rules and eligibility</p> */}
                        </div>
                    ) : (
                        <div style={{ width: 'calc(var(--max-content-width, 610px) - 4rem)', maxWidth: 'calc(100vw - 2rem)', background: 'var(--splitcard-white)', margin: '1rem', borderRadius: 'var(--border-radius-lrg)', padding: '2rem', textAlign: 'center', position: 'relative', overflowY: 'auto' }}>
                            <button
                                onClick={closeReferralDialog}
                                style={{
                                    position: 'absolute',
                                    top: '1rem',
                                    right: '1rem',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                aria-label="Close dialog"
                            >
                                <Icons.Close width="1.25rem" height="1.25rem" tint="var(--splitcard-gray-500)" />
                            </button>

                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                                <div style={{
                                    background: 'linear-gradient(135deg, #20c997 0%, #17a2b8 100%)',
                                    borderRadius: '1rem',
                                    padding: '1.25rem',
                                    display: 'inline-flex'
                                }}>
                                    <Icons.GiftBox width="2.5rem" height="2.5rem" tint="white" />
                                </div>
                            </div>

                            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem' }}>Give $5, Get $5</h2>
                            <p style={{ color: 'var(--splitcard-gray-600)', marginBottom: '1rem', fontSize: '0.875rem' }}>
                                Anyone you refer that creates and finalizes a group earns you both <strong style={{ color: 'var(--splitcard-green-600)' }}>$5 credit</strong>
                            </p>

                            <ol style={{
                                listStyle: 'none',
                                margin: 0,
                                padding: 0,
                                textAlign: 'left',
                                marginBottom: '2rem',
                                display: 'grid',
                                gridAutoRows: '1fr',
                                gap: '0.5rem',
                                '--badge-size': '2.5rem',
                                '--line-width': '2px'
                            } as React.CSSProperties}>
                                <li style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'var(--badge-size) 1fr',
                                    columnGap: '1rem',
                                    position: 'relative',
                                    alignItems: 'start'
                                }}>
                                    <div style={{
                                        background: '#EBF5FF',
                                        borderRadius: '0.5rem',
                                        padding: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '2.5rem',
                                        width: '2.5rem',
                                        height: '2.5rem',
                                        position: 'relative',
                                        zIndex: 2,
                                        border: '1px solid #BFDBFE'
                                    }}>
                                        <Icons.AddUser width="1.25rem" height="1.25rem" tint="#3B82F6" />
                                        <div style={{
                                            content: '""',
                                            position: 'absolute',
                                            left: 'calc(50% - 1px)',
                                            top: '100%',
                                            bottom: 'calc(-100% - 0.5rem)',
                                            width: '2px',
                                            background: 'var(--splitcard-gray-100)',
                                            zIndex: 0
                                        }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem' }}>Invite Someone New</h4>
                                        <p style={{ fontSize: '0.75rem', color: 'var(--splitcard-gray-600)', margin: 0 }}>Send your unique referral link to anyone.</p>
                                    </div>
                                </li>

                                <li style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'var(--badge-size) 1fr',
                                    columnGap: '1rem',
                                    position: 'relative',
                                    alignItems: 'start'
                                }}>
                                    <div style={{
                                        background: '#F3E8FF',
                                        borderRadius: '0.5rem',
                                        padding: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '2.5rem',
                                        width: '2.5rem',
                                        height: '2.5rem',
                                        position: 'relative',
                                        zIndex: 2,
                                        border: '1px solid #E9D5FF'
                                    }}>
                                        <Icons.Group width="1.25rem" height="1.25rem" tint="#A855F7" />
                                        <div style={{
                                            content: '""',
                                            position: 'absolute',
                                            left: 'calc(50% - 1px)',
                                            top: '100%',
                                            bottom: 'calc(-100% - 0.5rem)',
                                            width: '2px',
                                            background: 'var(--splitcard-gray-100)',
                                            zIndex: 0
                                        }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem' }}>They Create a Group</h4>
                                        <p style={{ fontSize: '0.75rem', color: 'var(--splitcard-gray-600)', margin: 0 }}>Your friend creates a group with at least 2 participants and spends $10 or more.</p>
                                    </div>
                                </li>

                                <li style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'var(--badge-size) 1fr',
                                    columnGap: '1rem',
                                    position: 'relative',
                                    alignItems: 'start'
                                }}>
                                    <div style={{
                                        background: '#D1FAE5',
                                        borderRadius: '0.5rem',
                                        padding: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '2.5rem',
                                        width: '2.5rem',
                                        height: '2.5rem',
                                        position: 'relative',
                                        zIndex: 2,
                                        border: '1px solid #86EFAC'
                                    }}>
                                        <Icons.Dollar width="1.25rem" height="1.25rem" tint="#10B981" />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem' }}>You Both Earn $5</h4>
                                        <p style={{ fontSize: '0.75rem', color: 'var(--splitcard-gray-600)', margin: 0 }}>Credit applied for future use to both your accounts once their group is finalized.</p>
                                    </div>
                                </li>
                            </ol>

                            <button
                                onClick={handleShareReferralLink}
                                style={{
                                    width: '100%',
                                    background: 'var(--splitcard-black)',
                                    color: 'var(--splitcard-white)',
                                    border: 'none',
                                    padding: '1rem',
                                    borderRadius: 'var(--border-radius-lrg)',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.75rem'
                                }}
                            >
                                <Icons.Share width="1.25rem" height="1.25rem" />
                                Share Link
                            </button>

                            {/* <p className='text-body-2 text-gray-500 text-center'>Rules and eligibility</p> */}
                        </div>
                    )}
                </div>
            </dialog>
            {/* <dialog ref={explainerRef}>
                <div>
                    <div className="flex flex-col items-stretch" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                        <ExplainerContent />
                        <Button theme="primary" formMethod="dialog" onClick={closeExplainer}>Got it!</Button>
                    </div>
                </div>
            </dialog> */}
            <dialog ref={spinnerDialogRef}>
                <div>
                    <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                        <Spinner />
                    </div>
                </div>
            </dialog>
            <dialog ref={ineligibleRef}>
                <div>
                    <div className="flex flex-col items-center" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'center', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.5rem', borderRadius: 'var(--border-radius-lrg)', overflowY: 'auto' }}>
                        <div className="bubble-md flex items-center justify-center" style={{ background: 'var(--splitcard-gray-200)', boxShadow: 'var(--box-shadow)' }}>
                            <Icons.Lock tint="var(--splitcard-gray-500)" width="2.5rem" height="2.5rem" />
                        </div>
                        <h2 style={{ margin: 0 }}>Not Eligible</h2>
                        <p style={{ color: 'var(--splitcard-gray-500)', margin: 0 }}>This referral promotion is available for new Splitcard users only.</p>
                        <Button theme="dark" onClick={closeIneligibleModal} style={{ width: '100%', marginTop: '0.5rem' }}>Got it</Button>
                    </div>
                </div>
            </dialog>
            <ServiceOutageModal state={[showOutage, setServiceOutage]} />
        </Column.Content>
    )
}


const DashboardRoute: RouteObject = {
    index: true,
    element: <AccountDashboardScreen />,
}

export { DashboardRoute }
